import React, { useEffect,useState, useMemo, use } from "react";
import { useSelector, useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { format } from "date-fns";
import { baseSelectStyles } from "../../shared/selectStyles";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Chip,
} from "@mui/material";
import LoadingDots from "../../shared/LoadingDots";
import { selectAuditEvents,selectAuditLoading, fetchAuditEvents} from "../../../ReduxSlices/AuditEvents/AuditEventSlice";

const filterOptions = [
{ value: "today", label: "Today" },
{ value: "month", label: "This Month" },
{ value: "year", label: "This Year" },
{ value: "custom", label: "Specific Day" },
{ value: "range", label: "Date Range" },
];

const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder}
    className="date-picker-input"
     style={{
      marginLeft: "10px",
      backgroundColor: "#E9F7EE",
      border: "1px solid #A5D6A7",
      borderRadius: "8px",
      padding: "0 4px",
      height: "30px",
      fontSize: "13px",
      cursor: "pointer",
    }}
  />
));


const AuditDashboard = () => {
  const events = useSelector(selectAuditEvents);
  const loaded = useSelector(state => state.audit.loaded);
  const dispatch = useDispatch();
  const isLoading = useSelector(selectAuditLoading);

  const [filter, setFilter] = useState("month");
  const [customDate, setCustomDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filteredEvents = useMemo(() => {
  const now = new Date();

  return events.filter((event) => {
    const eventDate = new Date(event.created_at);

    switch (filter) {
      case "today":
        return eventDate.toDateString() === now.toDateString();

      case "month":
        return (
          eventDate.getMonth() === now.getMonth() &&
          eventDate.getFullYear() === now.getFullYear()
        );

      case "year":
        return eventDate.getFullYear() === now.getFullYear();

      case "custom":
        return customDate
          ? eventDate.toDateString() === new Date(customDate).toDateString()
          : true;

      case "range":
        if (!startDate || !endDate) return false;

        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return eventDate >= start && eventDate <= end;

      default:
        return true;
    }
  });
}, [events, filter, customDate, startDate, endDate]);


  useEffect(() => {
    if (!loaded) {
      dispatch(fetchAuditEvents());
    }
  }, [dispatch, loaded]);


 return (
  <Box p={2}>
    <Typography variant="h6" mb={2}>
      Audit Trail
    </Typography>

    
        {/* FILTER UI */}
        <div className="flex items-center gap-4 mb-6 justify-center">
          <Select
            value={filterOptions.find((opt) => opt.value === filter)}
            onChange={(opt) => {
              setFilter(opt.value);
              if (opt.value !== "custom") setCustomDate("");
              if (opt.value !== "range") {
                setStartDate("");
                setEndDate("");
              }
            }}
            options={filterOptions}
            placeholder="Select filter"
            styles={baseSelectStyles}
          />

          {filter === "custom" && (
            <DatePicker
              selected={customDate ? new Date(customDate) : null}
              onChange={(date) =>
                setCustomDate(date ? date.toISOString().slice(0, 10) : "")
              }
              customInput={<CustomInput placeholder="Select a date" />}
              placeholderText="Select a date"
            />
          )}

          {filter === "range" && (
            <>
              <DatePicker
                selected={startDate ? new Date(startDate) : null}
                onChange={(date) =>
                  setStartDate(date ? date.toISOString().slice(0, 10) : "")
                }
                selectsStart
                startDate={startDate ? new Date(startDate) : null}
                endDate={endDate ? new Date(endDate) : null}
                customInput={<CustomInput placeholder="Start date" />}
                placeholderText="Start date"
              />

            <div className="ml-[30px]">
              <DatePicker
                selected={endDate ? new Date(endDate) : null}
                onChange={(date) =>
                  setEndDate(date ? date.toISOString().slice(0, 10) : "")
                }
                selectsEnd
                startDate={startDate ? new Date(startDate) : null}
                endDate={endDate ? new Date(endDate) : null}
                minDate={startDate ? new Date(startDate) : null}
                customInput={<CustomInput placeholder="End date" />}
                placeholderText="End date"
              />
            </div>
            </>
          )}
        </div>

    {isLoading ? (
      <LoadingDots />
    ) : filteredEvents.length === 0 ? (
      <Typography variant="body1">
        No audit events found for the selected period.
      </Typography>
    ) : (
      <Paper style={{ maxHeight: 500, overflow: "auto" }}>
        

        {/* LIST */}
        <List>
          {filteredEvents.map((event) => {
            const formattedDateTime = new Date(
              event.created_at
            ).toLocaleString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });

            return (
              <ListItem
                key={event.id}
                divider
                alignItems="flex-start"
              >
                <Box width="100%">
                  <Typography fontWeight={600}>
                    {event.message}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {event.context_line} • {formattedDateTime}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    )}
  </Box>
);
};

export default AuditDashboard;